import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Ban, ChevronDown, ChevronRight, FileText, PhoneCall, PhoneOutgoing, Plus, RefreshCw, Save, X,
} from 'lucide-react';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, EmptyState, ErrorState, MoneyStat, StatRow, StatusPill, TableSkeleton, fmtDateTime, humanise, num,
} from '../../components/ui';

// Rostery's own outbound AI sales calls (super admin): Jack rings a number and introduces Rostery.
// Shown as a tab of the AI receptionist console.

// Twilio call states → the app's status tones (utils/status.js meanings).
const CALL_TONE = {
  ringing: 'warning', 'in-progress': 'info', answered: 'info', completed: 'success',
  'no-answer': 'danger', busy: 'danger', failed: 'danger',
};
const callLabel = (s) => (s === 'canceled' ? 'Cancelled' : humanise(s) || '—');
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—');
const TH = 'px-4 py-2 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-slate-500 whitespace-nowrap';

function Disclosure({ icon: Icon, title, badge, open, onToggle, children }) {
  const bodyId = useId();
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={bodyId}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50">
        <Icon size={15} className="shrink-0 text-slate-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {badge}
        <ChevronDown size={16} aria-hidden="true"
          className={clsx('ml-auto shrink-0 text-slate-400 transition-transform duration-150', open && 'rotate-180')} />
      </button>
      <div id={bodyId} hidden={!open} className="space-y-3 px-4 pb-4">{children}</div>
    </div>
  );
}

export default function OutboundCalls({ embedded = false }) {
  const confirm = useConfirm();
  const [calls, setCalls]     = useState([]);
  const [stats, setStats]     = useState({ total: 0, answered: 0, completed: 0, no_answer: 0, leads: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [to, setTo]           = useState('');
  const [calling, setCalling] = useState(false);
  const [instr, setInstr]         = useState('');
  const [instrOpen, setInstrOpen] = useState(false);
  const [savingInstr, setSavingInstr] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dnc, setDnc]         = useState([]);
  const [dncOpen, setDncOpen] = useState(false);
  const [newDnc, setNewDnc]   = useState('');
  const [addingDnc, setAddingDnc] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/receptionist/outbound-calls');
      setCalls(data.calls || []);
      setStats(data.stats || {});
      setLoadError(null);
    } catch (e) {
      // A failed poll keeps the last list on screen; the error only takes over when there is nothing to show.
      setLoadError(e?.response?.data?.message || "We couldn't load the outbound calls. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, []);

  const loadInstr = useCallback(async () => {
    try { const { data } = await api.get('/receptionist/config'); setInstr(data.outbound_instructions || ''); } catch { /* ignore */ }
  }, []);

  const loadDnc = useCallback(async () => {
    try { const { data } = await api.get('/receptionist/dnc'); setDnc(data.entries || []); } catch { /* ignore */ }
  }, []);

  // Poll every 10s so Twilio status-callback updates (ringing → answered → completed) show live.
  useEffect(() => {
    load();
    loadInstr();
    loadDnc();
    timer.current = setInterval(load, 10000);
    return () => clearInterval(timer.current);
  }, [load, loadInstr, loadDnc]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const placeCall = async () => {
    const number = to.trim();
    if (!number) { toast.error('Enter a phone number'); return; }
    setCalling(true);
    try {
      const { data } = await api.post('/receptionist/outbound-call', { to: number });
      toast.success(data.message || 'Calling…');
      setTo('');
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not place the call');
    } finally { setCalling(false); }
  };

  const saveInstr = async () => {
    setSavingInstr(true);
    try {
      await api.put('/receptionist/config', { outbound_instructions: instr });
      toast.success('Outbound call instructions saved');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save the instructions');
    } finally { setSavingInstr(false); }
  };

  const addDnc = async () => {
    const phone = newDnc.trim();
    if (!phone) { toast.error('Enter a phone number'); return; }
    setAddingDnc(true);
    try {
      const { data } = await api.post('/receptionist/dnc', { phone });
      toast.success(data.message || 'Added to the Do-Not-Call list');
      setNewDnc('');
      loadDnc();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not add the number');
    } finally { setAddingDnc(false); }
  };

  const removeDnc = async (phone) => {
    const ok = await confirm(`Outbound AI calls will be able to dial ${phone} again.`, {
      title: 'Remove from the Do-Not-Call list?', confirmLabel: 'Remove number', danger: true,
    });
    if (!ok) return;
    try {
      await api.post('/receptionist/dnc/remove', { phone });
      setDnc((d) => d.filter((x) => x.phone !== phone));
      toast.success(`${phone} removed from the Do-Not-Call list`);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not remove the number');
    }
  };

  const inputCls = 'h-9 rounded-lg border border-slate-200 bg-white px-3 text-[13px] text-slate-800 placeholder:text-slate-400 hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

  const body = (
    <div className="space-y-5">
      {/* Action row */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-[13px] text-slate-600">Jack calls a number and introduces Rostery.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+61…" aria-label="Phone number to call"
            onKeyDown={(e) => { if (e.key === 'Enter') placeCall(); }} className={clsx(inputCls, 'w-44')} />
          <button type="button" onClick={placeCall} disabled={calling || !to.trim()} className="btn-primary btn-sm h-9">
            {calling ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <PhoneCall size={14} aria-hidden="true" />}
            {calling ? 'Calling…' : 'Place AI call'}
          </button>
          <button type="button" onClick={refresh} disabled={refreshing} className="btn-secondary btn-sm h-9">
            <RefreshCw size={14} className={clsx((refreshing || loading) && 'animate-spin')} aria-hidden="true" /> Refresh
          </button>
        </div>
      </div>

      {/* Outbound call instructions — feed Jack's script on every outbound call */}
      <Disclosure icon={FileText} title="Outbound call instructions" open={instrOpen} onToggle={() => setInstrOpen((o) => !o)}
        badge={instr ? <span className="badge-green !px-2 !text-[11px]">Set</span> : null}>
        <p className="text-[13px] leading-relaxed text-slate-600">
          Extra instructions Jack follows on every outbound call: what to pitch, tone, current offers and who to ask for.
          Leave blank to use the built-in script.
        </p>
        <textarea value={instr} onChange={(e) => setInstr(e.target.value)} rows={6} aria-label="Outbound call instructions"
          placeholder="For example: mention our June free-setup offer, lead with SCHADS payroll pain, and ask to speak with the operations manager."
          className="input-field" />
        <div className="flex justify-end">
          <button type="button" onClick={saveInstr} disabled={savingInstr} className="btn-primary btn-sm h-9">
            {savingInstr ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
            {savingInstr ? 'Saving…' : 'Save instructions'}
          </button>
        </div>
      </Disclosure>

      {/* Do-Not-Call list */}
      <Disclosure icon={Ban} title="Do-Not-Call list" open={dncOpen} onToggle={() => setDncOpen((o) => !o)}
        badge={dnc.length ? <span className="badge-gray !px-2 !text-[11px] tabular-nums">{num(dnc.length)}</span> : null}>
        <p className="text-[13px] leading-relaxed text-slate-600">
          Numbers here are never dialled by outbound AI calls. Callers who ask to be removed are added automatically.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="tel" value={newDnc} onChange={(e) => setNewDnc(e.target.value)} placeholder="+61…" aria-label="Number to add to the Do-Not-Call list"
            onKeyDown={(e) => { if (e.key === 'Enter') addDnc(); }} className={clsx(inputCls, 'w-44')} />
          <button type="button" onClick={addDnc} disabled={addingDnc || !newDnc.trim()} className="btn-secondary btn-sm h-9">
            {addingDnc ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />} Add number
          </button>
        </div>
        {!dnc.length ? (
          <p className="text-[13px] text-slate-500">No numbers on the list yet.</p>
        ) : (
          <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
            {dnc.map((d) => (
              <li key={d.phone} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13px]">
                <span className="font-medium tabular-nums text-slate-800">{d.phone}</span>
                <span className="flex min-w-0 items-center gap-2">
                  {d.reason && <span className="hidden max-w-[220px] truncate text-[12.5px] text-slate-500 sm:inline" title={d.reason}>{d.reason}</span>}
                  <button type="button" onClick={() => removeDnc(d.phone)} aria-label={`Remove ${d.phone} from the Do-Not-Call list`}
                    className="rounded-lg p-2 text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-600">
                    <X size={15} aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Disclosure>

      {/* Totals */}
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
        <StatRow cols={5}>
          <MoneyStat label="Total calls" value={num(stats.total || 0)} currency={false} />
          <MoneyStat label="Answered" value={num(stats.answered || 0)} currency={false} />
          <MoneyStat label="Completed" value={num(stats.completed || 0)} currency={false} />
          <MoneyStat label="No answer" value={num(stats.no_answer || 0)} currency={false} />
          <MoneyStat label="Leads captured" value={num(stats.leads || 0)} currency={false} />
        </StatRow>
      </div>

      {/* Compliance note */}
      <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-slate-600">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
        Automated outbound calls are subject to Australian consent and Do-Not-Call rules. Only call contacts who have opted in.
      </p>

      {/* Calls */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading && !calls.length ? (
          <TableSkeleton rows={5} cols={6} />
        ) : loadError && !calls.length ? (
          <ErrorState message={loadError} onRetry={refresh} />
        ) : !calls.length ? (
          <EmptyState icon={PhoneOutgoing} title="No outbound calls yet" hint="Enter a number above and choose Place AI call." />
        ) : (
          <>
            {loadError && (
              <p className="border-b border-slate-100 px-4 py-2 text-[12.5px] text-amber-700" role="status">
                Couldn't refresh the calls. Showing the last list loaded.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <caption className="sr-only">Outbound AI calls</caption>
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className={TH}>Number</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={TH}>Duration</th>
                    <th scope="col" className={TH}>Lead</th>
                    <th scope="col" className={TH}>Outcome or summary</th>
                    <th scope="col" className={TH}>By</th>
                    <th scope="col" className={TH}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => {
                    const hasT = Array.isArray(c.transcript) && c.transcript.length > 0;
                    const open = expandedId === c.id;
                    return (
                      <React.Fragment key={c.id}>
                        <tr className="border-b border-slate-100">
                          <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800">
                            {hasT ? (
                              <button type="button" onClick={() => setExpandedId(open ? null : c.id)} aria-expanded={open}
                                className="-mx-1.5 inline-flex min-h-[32px] items-center gap-1.5 rounded-md px-1.5 transition-colors duration-150 hover:bg-slate-100">
                                {open ? <ChevronDown size={14} className="text-slate-400" aria-hidden="true" />
                                  : <ChevronRight size={14} className="text-slate-400" aria-hidden="true" />}
                                <span className="sr-only">{open ? 'Hide the transcript for ' : 'Show the transcript for '}</span>
                                <span className="tabular-nums">{c.to_number}</span>
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="inline-block w-3.5" aria-hidden="true" />
                                <span className="tabular-nums">{c.to_number}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <StatusPill status={c.status || 'unknown'} tone={CALL_TONE[c.status] || 'neutral'} label={callLabel(c.status)} />
                          </td>
                          <td className="px-4 py-2 tabular-nums text-slate-700">{fmtDur(c.duration_seconds)}</td>
                          <td className="px-4 py-2">
                            {c.lead_captured ? <span className="text-[12.5px] font-semibold text-emerald-700">Yes</span> : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="px-4 py-2 text-slate-700">
                            <span className="block max-w-[340px] truncate" title={c.summary || ''}>{c.summary || '—'}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-[12.5px] text-slate-500">{c.triggered_by || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-2 text-[12.5px] tabular-nums text-slate-500">{fmtDateTime(c.created_at)}</td>
                        </tr>
                        {open && hasT && (
                          <tr className="border-b border-slate-100 bg-slate-50/70">
                            <td colSpan={7} className="px-6 py-3">
                              <p className="mb-1.5 text-[12px] font-semibold text-slate-500">Call transcript</p>
                              <div className="max-h-72 space-y-1.5 overflow-y-auto pr-2">
                                {c.transcript.map((m, i) => (
                                  <p key={i} className="text-[12.5px] leading-relaxed">
                                    <span className="font-semibold text-slate-800">{m.role === 'assistant' ? 'Jack' : 'Caller'}:</span>{' '}
                                    <span className="text-slate-600">{m.content}</span>
                                  </p>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return embedded ? body : <div className={PAGE_WIDE}>{body}</div>;
}
